import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/use-toast";
import { Download, MoreHorizontal, Loader2, Menu, AlertCircle } from "lucide-react";
import { ToastAction } from "@/components/ui/toast";
import MenuBar from "@/components/MenuBar";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import type {
  Employee,
  EmployeePayrunResponse,
  ApprovePayrunResponse,
  PayrunReadinessCheckResponse,
  PayrunStatus,
  UnapprovePayrunResponse,
} from "@/types/payrun";
import { ScrollArea } from "./ui/scroll-area";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import SuperUserEmployerSelector from "@/components/common/SuperUserEmployerSelector";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface RowMenuProps {
  onDownloadPaystub: () => void;
}

const RowMenu: React.FC<RowMenuProps> = ({ onDownloadPaystub }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onDownloadPaystub}>
          <Download className="mr-2 h-4 w-4" />
          Download Paystub
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const PayrunApproval: React.FC = () => {
  const { payrunId } = useParams<{ payrunId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isPayrunReady, setIsPayrunReady] = useState(false);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [payrunStatus, setPayrunStatus] = useState<PayrunStatus | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUnapproveDialog, setShowUnapproveDialog] = useState(false);
  const [isUnapproving, setIsUnapproving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  
  // Get the userId and selected employees from URL query params
  const urlParams = new URLSearchParams(location.search);
  const urlUserId = urlParams.get('userId');
  const urlSelected = urlParams.get('selected');
  
  console.log("PayrunApproval: URL search params:", location.search);
  console.log("PayrunApproval: Extracted userId from URL:", urlUserId);
  
  // Initialize state with the URL userId if present, otherwise use "current"
  const [selectedUserId, setSelectedUserId] = useState<string>(urlUserId || "current");
  const isSuperUserAccount = isSuperUser(user);
  
  console.log("PayrunApproval: Using selectedUserId:", selectedUserId);

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
    
    // Update URL when user selection changes without causing a navigation
    const newUrl = new URL(window.location.href);
    if (userId !== "current") {
      newUrl.searchParams.set('userId', userId);
    } else {
      newUrl.searchParams.delete('userId');
    }
    window.history.replaceState({}, '', newUrl.toString());
    
    // Refresh data with the new user context
    fetchEmployeesForPayrun();
    checkPayrunReadiness();
  };

  useEffect(() => {
    if (payrunId) {
      fetchEmployeesForPayrun();
      checkPayrunReadiness();
    }
  }, [payrunId, selectedUserId]);

  // Apply preselected employees from query param on first load
  useEffect(() => {
    if (urlSelected && employees.length > 0 && selected.length === 0) {
      const ids = urlSelected.split(',').filter(Boolean);
      const validIds = employees
        .map((e) => e.id.toString())
        .filter((id) => ids.includes(id));
      if (validIds.length > 0) setSelected(validIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSelected, employees]);

  const checkPayrunReadiness = async () => {
    try {
      if (!payrunId) return;

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<PayrunReadinessCheckResponse>(
        EndpointKeys.CHECK_PAYRUN_READINESS,
        {
          urlParams: { id: payrunId },
          queryParams,
        },
      );

      setIsPayrunReady(response.isReady);

      if (!response.isReady && !isViewOnly) {
        const failedChecks = response.checks.filter((check) => !check.passed);
        failedChecks.forEach((check) => {
          if (check.checkName === "monthly_submission_review") {
            // Hard error for submitted but unreviewed submissions
            toast({
              variant: "destructive",
              title: "Monthly Submission Review Required",
              description: check.message,
              action: (
                <ToastAction altText="Review Submission" onClick={() => navigate("/employee-submissions")}>
                  Review Submission
                </ToastAction>
              ),
            });
          } else if (check.checkName === "monthly_submission_status") {
            // Soft warning for unsubmitted submissions
            toast({
              variant: "default",
              title: "Monthly Submission Not Submitted",
              description: check.message,
              action: (
                <ToastAction altText="View Submissions" onClick={() => navigate("/employee-submissions")}>
                  View Submissions
                </ToastAction>
              ),
            });
          } else {
            // Other hard errors
            toast({
              variant: "destructive",
              title: `Failed check: ${check.checkName}`,
              description: check.message,
            });
          }
        });
      }
      
      // Also show soft warnings even when payrun is ready
      if (!isViewOnly) {
        const softWarnings = response.checks.filter((check) => 
          !check.passed && check.checkName === "monthly_submission_status"
        );
        softWarnings.forEach((check) => {
          toast({
            variant: "default",
            title: "Monthly Submission Not Submitted",
            description: check.message,
            action: (
              <ToastAction altText="View Submissions" onClick={() => navigate("/employee-submissions")}>
                View Submissions
              </ToastAction>
            ),
          });
        });
      }
    } catch (error) {
      console.error("Error checking payrun readiness:", error);
      if (!isViewOnly) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to check payrun readiness",
        });
      }
    }
  };

  const fetchEmployeesForPayrun = async () => {
    try {
      if (!payrunId) return;
      setIsLoading(true);
      
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }
      
      const data = await ApiService.request<EmployeePayrunResponse>(
        EndpointKeys.GET_PAYRUN_EMPLOYEES,
        {
          urlParams: { id: payrunId },
          queryParams,
        },
      );
      // Only include active employees in approval
      const activeEmployees = (data.employees as any[]).filter((e) => e?.isActive !== false);
      setEmployees(activeEmployees);
      setPayrunStatus(data.employees[0].status);
      setIsViewOnly(data.employees[0].status !== "Draft");
    } catch (error) {
      console.error("Error fetching employees for payrun:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch employees for payrun",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPaystub = async (plivId: number) => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }
      
      await ApiService.downloadBlob(EndpointKeys.DOWNLOAD_PAYSTUB, {
        urlParams: { id: plivId },
        queryParams,
      });
      toast({
        title: "Success",
        description: "Paystub downloaded successfully",
      });
    } catch (error) {
      console.error("Error downloading paystub:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to download paystub",
      });
    }
  };

  const handleApprovePayroll = async () => {
    try {
      if (!payrunId || !isPayrunReady || isViewOnly) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Payrun is not ready for approval",
        });
        return;
      }

      setIsApproving(true);

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<ApprovePayrunResponse>(
        EndpointKeys.APPROVE_PAYRUN,
        {
          method: "POST",
          urlParams: { id: payrunId },
          body: { state: "APPROVED" },
          queryParams,
        },
      );

      if (response.success) {
        toast({
          title: "Success",
          description: "Payroll approved successfully",
        });
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error approving payroll:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to approve payroll",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleUnapprovePayrun = async () => {
    try {
      if (!payrunId) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Invalid payrun ID",
        });
        return;
      }

      setIsUnapproving(true);

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<UnapprovePayrunResponse>(
        EndpointKeys.UNAPPROVE_PAYRUN,
        {
          method: "POST",
          urlParams: { id: payrunId },
          queryParams,
        },
      );

      toast({
        title: "Success",
        description: "Payrun has been unapproved and returned to draft status",
      });

      // Refresh the page data
      await fetchEmployeesForPayrun();
      await checkPayrunReadiness();
      
      // Close the dialog
      setShowUnapproveDialog(false);
    } catch (error: any) {
      console.error("Error unapproving payrun:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.body?.error || "Failed to unapprove payrun",
      });
    } finally {
      setIsUnapproving(false);
    }
  };

  const calculateGrossPay = (earnings: Employee["earnings"]): number => {
    return earnings.reduce((total, earning) => {
      const amount = parseFloat(earning.amount.currentAmount);
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);
  };

  const calculateEmployeeTaxes = (taxes: Employee["taxes"]): number => {
    return taxes
      .filter((tax) => tax.isEmployee)
      .reduce((total, tax) => {
        const amount = parseFloat(tax.currentAmount);
        return total + (isNaN(amount) ? 0 : amount);
      }, 0);
  };

  const calculateEmployerTaxes = (taxes: Employee["taxes"]): number => {
    return taxes
      .filter((tax) => !tax.isEmployee)
      .reduce((total, tax) => {
        const amount = parseFloat(tax.currentAmount);
        return total + (isNaN(amount) ? 0 : amount);
      }, 0);
  };

  const calculateNetPay = (grossPay: number, employeeTaxes: number): number => {
    return grossPay - employeeTaxes;
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0 p-4 md:p-6 overflow-y-auto bg-gray-100">
        <div className="max-w-7xl">
          {isSuperUserAccount && (
            <div className="mb-6 flex justify-end">
              <SuperUserEmployerSelector
                selectedUserId={selectedUserId}
                onUserChange={handleUserChange}
              />
            </div>
          )}
          
          <h1 className="text-3xl font-bold mb-6">
            {isViewOnly
              ? "Payroll Details"
              : "Approve Payroll"}
          </h1>

          <Card>
            <CardHeader>
              <CardTitle>
                {isViewOnly ? "View Payrun Details" : "Payrun Overview"}
              </CardTitle>
              {payrunStatus && (
                <div className="text-sm text-muted-foreground">
                  Status: {payrunStatus}
                </div>
              )}
            </CardHeader>
            <CardContent>
              <ScrollArea className="w-full">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          {!isViewOnly && (
                            <Checkbox
                              checked={selected.length === employees.length}
                              onCheckedChange={(checked) => {
                                setSelected(
                                  checked
                                    ? employees.map((e) => e.id.toString())
                                    : [],
                                );
                              }}
                            />
                          )}
                        </TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="text-right hidden md:table-cell">
                          Gross Pay
                        </TableHead>
                        <TableHead className="text-right hidden md:table-cell">
                          Employee Taxes
                        </TableHead>
                        <TableHead className="text-right hidden md:table-cell">
                          Employer Taxes
                        </TableHead>
                        <TableHead className="text-right">Net Pay</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employees.map((employee) => {
                        const grossPay = calculateGrossPay(employee.earnings);
                        const employeeTaxes = calculateEmployeeTaxes(
                          employee.taxes,
                        );
                        const employerTaxes = calculateEmployerTaxes(
                          employee.taxes,
                        );
                        const netPay = calculateNetPay(grossPay, employeeTaxes);

                        return (
                          <TableRow key={employee.id}>
                            <TableCell>
                              {!isViewOnly && (
                                <Checkbox
                                  checked={selected.includes(
                                    employee.id.toString(),
                                  )}
                                  onCheckedChange={(checked) => {
                                    setSelected((prev) =>
                                      checked
                                        ? [...prev, employee.id.toString()]
                                        : prev.filter(
                                            (id) =>
                                              id !== employee.id.toString(),
                                          ),
                                    );
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {employee.firstName} {employee.lastName}
                            </TableCell>
                            <TableCell className="text-right hidden md:table-cell">
                              £{grossPay.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right hidden md:table-cell">
                              £{employeeTaxes.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right hidden md:table-cell">
                              £{employerTaxes.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">
                              £{netPay.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">
                              <RowMenu
                                onDownloadPaystub={() =>
                                  handleDownloadPaystub(employee.plivId)
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
              {!isViewOnly && (
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={handleApprovePayroll}
                    disabled={!isPayrunReady || isApproving}
                    className="w-full md:w-auto"
                    data-testid="approve-payroll-button"
                  >
                    {isApproving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Approving...
                      </>
                    ) : isPayrunReady ? (
                      "Approve Payroll"
                    ) : (
                      "Not Ready for Approval"
                    )}
                  </Button>
                </div>
              )}
              {isViewOnly && payrunStatus === "Approved" && (
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={() => setShowUnapproveDialog(true)}
                    variant="destructive"
                    className="w-full md:w-auto"
                    data-testid="unapprove-button"
                  >
                    Unapprove Payrun
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Unapprove Confirmation Dialog */}
      <Dialog open={showUnapproveDialog} onOpenChange={setShowUnapproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Confirm Unapprove Payrun
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-3">
                <p>
                  Are you sure you want to unapprove this payrun? This action will:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Revert the payrun back to draft status</li>
                  <li>Archive the automatically generated draft payrun</li>
                  <li>Reopen the monthly submission for editing</li>
                  <li>Archive any related filings</li>
                </ul>
                <p className="font-medium text-destructive">
                  Note: This action can only be performed before the pay date.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUnapproveDialog(false)}
              disabled={isUnapproving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnapprovePayrun}
              disabled={isUnapproving}
              data-testid="confirm-unapprove-button"
            >
              {isUnapproving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Unapproving...
                </>
              ) : (
                "Confirm Unapprove"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrunApproval;
